// The one place where API enum values (English) become Indonesian labels. Tones: constants/tones.ts.
import { FILL_TONE, LIFECYCLE_TONE, POSITION_TONE, SEVERITY_TONE, SHIPMENT_KIND_TONE, SHIPMENT_STATUS_TONE, TEST_RESULT_TONE, WAREHOUSE_TONE, type Tone } from '@constants/tones';
import type { AuditCategory, AuditSource } from '@lib/api/audit';
import type { CitizenRole, HouseholdSource, HouseholdStatus } from '@lib/api/households';
import type { IdMethod } from '@lib/api/cylinderLookup';
import type { DeliveryFailure, EmptyCondition, Fulfilment, OrderActor, OrderCancelReason, OrderChannel, OrderKind, OrderStatus } from '@lib/api/orders';
import type { ApplicantType, CheckResult, HubApplicationStatus, HubDocumentKind, HubStatus, InternetType, LandStatus, RejectReasonCode, RevisableField, SanctionKind } from '@lib/api/hubRegistration';
import { ALERT_TYPE_TEXT, SEVERITY_TEXT } from './alerts';
import {
  OWNER_WAREHOUSE,
  type CylinderStatus,
  type FillState,
  type LocationType,
  type PositionStatus,
  type RejectReason,
  type Role,
  type ScopeKind,
  type Severity,
  type ShipmentKind,
  type ShipmentStatus,
  type TestResult,
  type WithdrawalReason,
} from '@lib/api/common';

/** In the order of the distribution chain (the location form lists them this way). */
export const LOCATION_TYPE: Record<LocationType, string> = {
  OWNER_WAREHOUSE: 'Gudang Pusat',
  FILLING_STATION: 'SPBG',
  HUB: 'Hub',
  TEST_WORKSHOP: 'Bengkel Uji',
};
export const locationTypeLabel = (t: string | null | undefined) => (t ? (LOCATION_TYPE[t as LocationType] ?? t) : '–');
export const isWarehouse = (t: string | null | undefined) => t === OWNER_WAREHOUSE;

// --- cylinder position (where it is) ------------------------------------------------------------
export const POSITION_LABEL: Record<PositionStatus, string> = {
  AT_LOCATION: 'Di lokasi',
  IN_TRANSIT: 'Dalam pengiriman',
  MISSING: 'Tidak sampai',
  WITH_CONSUMER: 'Di warga',
  WITH_COURIER: 'Dibawa kurir',
};
/** "Di gudang" (grey) when the location is the owner warehouse, otherwise the plain position label. */
export function positionInfo(p: PositionStatus, locationType?: string | null): { label: string; tone: Tone } {
  if (p === 'AT_LOCATION' && isWarehouse(locationType)) return { label: 'Di gudang', tone: WAREHOUSE_TONE };
  return { label: POSITION_LABEL[p], tone: POSITION_TONE[p] };
}

// --- cylinder lifecycle -------------------------------------------------------------------------
export const LIFECYCLE_LABEL: Record<CylinderStatus, string> = { ACTIVE: 'Aktif', WITHDRAWN: 'Ditarik', DAMAGED: 'Rusak', LOST: 'Hilang', DESTROYED: 'Dimusnahkan' };

// --- what a cylinder holds ----------------------------------------------------------------------
export const FILL_STATE_LABEL: Record<FillState, string> = { FULL: 'Isi', EMPTY: 'Kosong' };
/** Label and tone of a fill state. */
export const fillInfo = (s: FillState) => ({ label: FILL_STATE_LABEL[s], tone: FILL_TONE[s] });

// --- Bengkel Uji ----------------------------------------------------------------------------------
export const TEST_RESULT_LABEL: Record<TestResult, string> = { PASS: 'Lulus', FAIL: 'Gagal' };
export const testResultInfo = (r: TestResult) => ({ label: TEST_RESULT_LABEL[r], tone: TEST_RESULT_TONE[r] });

// --- Surat Jalan --------------------------------------------------------------------------------
export const SHIPMENT_STATUS_LABEL: Record<ShipmentStatus, string> = {
  LOADING: 'Memuat',
  IN_TRANSIT: 'Dalam pengiriman',
  RECEIVED: 'Diterima',
  RECEIVED_WITH_DISCREPANCY: 'Diterima dengan selisih',
  CANCELLED: 'Dibatalkan',
};
/** Long label ("Diterima dengan selisih 2") or the short chip ("Selisih 2", "Di jalan"). */
export function shipmentStatus(status: ShipmentStatus, missing: number, short = false): { label: string; tone: Tone } {
  const tone = SHIPMENT_STATUS_TONE[status];
  if (status === 'RECEIVED_WITH_DISCREPANCY') return { label: short ? `Selisih ${missing}` : `Diterima dengan selisih ${missing}`, tone };
  if (status === 'IN_TRANSIT' && short) return { label: 'Di jalan', tone };
  return { label: SHIPMENT_STATUS_LABEL[status] ?? status, tone };
}
export const shipmentNumber = (n: string | null | undefined) => n ?? 'Draf';

/** A Surat Jalan's kind: the badge, the filter, the operator's first step. */
export const SHIPMENT_KIND_LABEL: Record<ShipmentKind, string> = { DISTRIBUTION: 'Distribusi', WITHDRAWAL: 'Penarikan' };
/** Label and tone of a kind (an older backend sends none: a distribution note). */
export const shipmentKindInfo = (k: ShipmentKind | null | undefined) => {
  const kind = k ?? 'DISTRIBUTION';
  return { kind, label: SHIPMENT_KIND_LABEL[kind], tone: SHIPMENT_KIND_TONE[kind] };
};
/** Why a cylinder is withdrawn (per cylinder on a Penarikan). */
export const WITHDRAWAL_REASON_LABEL: Record<WithdrawalReason, string> = { RETEST: 'Uji berkala', DAMAGED: 'Rusak' };

export const REJECT_LABEL: Record<RejectReason, string> = {
  UNREGISTERED: 'Belum terdaftar',
  NOT_ACTIVE: 'Tidak aktif',
  NOT_AT_ORIGIN: 'Tidak tercatat di lokasi ini',
  ON_OTHER_SHIPMENT: 'Di Surat Jalan lain',
  TEST_OVERDUE: 'Lewat jadwal uji ulang',
  NOT_IN_SHIPMENT: 'Bukan bagian kiriman',
};

/** Cylinder kind as typed at registration ("CNG Type 4") shown the way the design says it. */
export const kindLabel = (k: string | null | undefined) => (!k ? '–' : /type\s*4/i.test(k) ? 'Tipe 4 (komposit)' : k.replace(/type/i, 'Tipe'));
export const KIND_TYPE4 = 'CNG Type 4';

// --- people, alerts, gateways -------------------------------------------------------------------
export const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', viewer: 'Pemantau', regulator: 'Pengawas', operator: 'Petugas', field_verifier: 'Verifikator Lapangan', courier: 'Kurir' };
/** One line per role: the role cards of the user form, the account page. */
export const ROLE_HELP: Record<Role, string> = {
  admin: 'Mengelola titik distribusi, reader, tabung, dan pengguna di wilayahnya.',
  viewer: 'Melihat ringkasan, Surat Jalan, tabung, titik distribusi, reader, dan peringatan.',
  regulator: 'Regulator (ESDM/Lemigas): memantau dan menelusuri tanpa mengubah; data pribadi disamarkan.',
  operator: 'Memuat, menerima, dan mendaftarkan tabung, atau mencatat hasil uji, di satu titik distribusi.',
  field_verifier: 'Mengunjungi lokasi calon Hub yang ditugaskan dan mengisi daftar periksa di lapangan.',
  courier: 'Mengantar pesanan warga dari satu Hub dan mencatat penukaran tabung di rumah warga.',
};
export const SCOPE_LABEL: Record<ScopeKind, string> = { national: 'Nasional', province: 'Provinsi', location: 'Titik Distribusi' };

/** Tingkat (Kritis · Penting · Informasi): the alerts copy owns the words. */
export const SEVERITY_LABEL: Record<Severity, string> = SEVERITY_TEXT;
/** Label and tone of an alert severity. */
export const severityInfo = (s: Severity) => ({ label: SEVERITY_LABEL[s], tone: SEVERITY_TONE[s] });
/** Label and tone of a lifecycle status. */
export const lifecycleInfo = (s: CylinderStatus) => ({ label: LIFECYCLE_LABEL[s], tone: LIFECYCLE_TONE[s] });
export const ALERT_LABEL: Record<string, string> = ALERT_TYPE_TEXT;
/** A shipment between two locations that are not in one parent/child line of the network (chip and banner). */
export const OFF_NETWORK = {
  chip: 'Di luar jaringan',
  tip: 'Asal dan tujuan tidak satu jalur induk–bawahan di jaringan distribusi. Kiriman tetap sah, tapi tercatat sebagai peringatan.',
} as const;

export const EVENT_LABEL: Record<string, string> = {
  TAG_SEEN: 'Tag terbaca',
  TAG_GONE: 'Tag keluar jangkauan',
  TAG_TID: 'TID terbaca',
  READER_STATUS: 'Status reader',
  GATEWAY_START: 'Reader mulai bekerja',
};

export const COMMAND_LABEL: Record<string, string> = {
  beep: 'Bunyikan reader',
  get_info: 'Periksa reader',
  set_power: 'Ubah daya antena',
};

export const AUDIT_ACTION: Record<string, string> = {
  login: 'Masuk',
  'login.failed': 'Gagal masuk',
  logout: 'Keluar',
  'session.sign_out': 'Mengeluarkan perangkat',
  'session.sign_out_others': 'Mengeluarkan perangkat lain',
  'user.rename': 'Mengganti nama sendiri',
  'audit.export': 'Mengunduh log aktivitas',
  'seed.locations': 'Mengisi contoh Titik Distribusi',
  'seed.demo': 'Mengisi data demo',
  'seed.clear_demo': 'Menghapus data demo',
  'seed.national': 'Mengisi data uji skala nasional',
  'seed.rebuild_event_daily': 'Menghitung ulang rekap pembacaan harian',
  'system.import': 'Mengimpor data dari database lama',
  'user.delete': 'Menghapus pengguna',
  'location.photo_add': 'Menambah foto Titik Distribusi',
  'location.photo_update': 'Mengubah foto Titik Distribusi',
  'location.photo_remove': 'Menghapus foto Titik Distribusi',
  'user.create': 'Membuat pengguna',
  'user.update': 'Mengubah pengguna',
  'user.reset_password': 'Membuat kata sandi baru',
  'user.change_password': 'Mengganti kata sandi sendiri',
  'totp.enabled': 'Mengaktifkan verifikasi dua langkah',
  'totp.disabled': 'Menonaktifkan verifikasi dua langkah',
  'totp.reset': 'Mengatur ulang verifikasi dua langkah',
  'totp.recovery_regenerated': 'Membuat ulang kode pemulihan',
  'totp.recovery_used': 'Masuk dengan kode pemulihan',
  'cylinder.create': 'Mendaftarkan tabung',
  'cylinder.update': 'Mengubah data tabung',
  'cylinder.delete': 'Menghapus tabung',
  'cylinder.retag': 'Mengganti tag tabung',
  'cylinder.found': 'Menandai tabung ditemukan',
  'cylinder.test': 'Mencatat hasil uji tabung',
  'location.create': 'Menambah Titik Distribusi',
  'location.update': 'Mengubah Titik Distribusi',
  'location.delete': 'Menghapus Titik Distribusi',
  'gateway.update': 'Menempatkan gateway',
  'gateway.delete': 'Menghapus gateway',
  'gateway.command': 'Mengirim perintah ke gateway',
  'alert.ack': 'Menindaklanjuti peringatan',
  'alert.close': 'Menutup peringatan',
  'ota.upload': 'Versi gateway baru diterima',
  'ota.rollout': 'Memasang versi gateway',
  'ota.cancel': 'Membatalkan pembaruan',
  'ota.delete': 'Menghapus versi gateway',
  'shipment.create': 'Membuat Surat Jalan',
  'shipment.item.add': 'Menambah tabung manual',
  'shipment.item.remove': 'Mengeluarkan tabung dari muatan',
  'shipment.item.reason': 'Mengubah alasan penarikan',
  'shipment.item.receive': 'Menerima tabung manual',
  'shipment.pause': 'Menjeda pemindaian',
  'shipment.resume': 'Melanjutkan pemindaian',
  'shipment.depart': 'Memberangkatkan kiriman',
  'shipment.cancel': 'Membatalkan Surat Jalan',
  'shipment.receive.start': 'Mulai membongkar',
  'shipment.receive.finish': 'Menyelesaikan penerimaan',
};
export const AUDIT_CATEGORY: Record<AuditCategory, string> = {
  account: 'Akun & masuk',
  session: 'Akun & masuk',
  user: 'Pengguna',
  location: 'Titik Distribusi',
  gateway: 'Reader',
  cylinder: 'Tabung',
  shipment: 'Surat Jalan',
  alert: 'Peringatan',
  ota: 'Pembaruan',
  system: 'Sistem',
};
/** Who did it, when it was not a signed-in user. */
export const AUDIT_SOURCE: Record<Exclude<AuditSource, 'user'>, string> = {
  cli: 'Sistem (CLI)',
  ci: 'Sistem (rilis otomatis)',
  anonymous: 'Tidak dikenal',
  deleted: 'Pengguna terhapus',
  system: 'Sistem',
};
/** Why a sign-in failed (login.failed). */
export const LOGIN_FAILURE: Record<string, string> = {
  unknown_email: 'Email tidak terdaftar',
  wrong_password: 'Kata sandi salah',
  totp_invalid: 'Kode verifikasi salah',
  client_outdated: 'Aplikasi perlu dimuat ulang',
  inactive: 'Akun nonaktif',
  rate_limited: 'Terlalu banyak percobaan',
};

// --- Hub registration (Phase 2) -----------------------------------------------------------------------------------
export const APPLICANT_TYPE_LABEL: Record<ApplicantType, string> = {
  INDIVIDUAL: 'Perorangan',
  COOPERATIVE: 'Koperasi',
  VILLAGE_ENTERPRISE: 'BUMDes',
  LIMITED_PARTNERSHIP: 'CV',
  LIMITED_COMPANY: 'PT',
};
export const APPLICANT_TYPE_HELP: Record<ApplicantType, string> = {
  INDIVIDUAL: 'Usaha mikro atau kecil milik perorangan.',
  COOPERATIVE: 'Koperasi berbadan hukum, termasuk Koperasi Desa/Kelurahan Merah Putih.',
  VILLAGE_ENTERPRISE: 'Badan Usaha Milik Desa.',
  LIMITED_PARTNERSHIP: 'Persekutuan komanditer.',
  LIMITED_COMPANY: 'Perseroan terbatas.',
};
export const HUB_DOCUMENT_LABEL: Record<HubDocumentKind, string> = {
  IDENTITY_CARD: 'KTP penanggung jawab',
  TAX_ID: 'NPWP',
  BUSINESS_ID: 'Nomor Induk Berusaha (NIB)',
  DEED_OF_ESTABLISHMENT: 'Akta pendirian dan perubahannya',
  LEGAL_ENTITY_DECREE: 'SK pengesahan badan hukum dari Kementerian Hukum',
  AUTHORITY_LETTER: 'Surat kuasa/penunjukan penanggung jawab',
  VILLAGE_RECOMMENDATION: 'Surat rekomendasi kepala desa/lurah/camat',
  LAND_PROOF: 'Bukti penguasaan lahan',
  ENVIRONMENTAL_STATEMENT: 'Surat Pernyataan Pengelolaan Lingkungan (SPPL)',
  SITE_PHOTO_FRONT: 'Foto tampak depan lokasi',
  SITE_PHOTO_STORAGE: 'Foto ruang penyimpanan',
  SITE_PHOTO_SURROUNDINGS: 'Foto lingkungan sekitar',
  SITE_SKETCH: 'Denah lokasi',
  INTEGRITY_STATEMENT: 'Surat pernyataan kebenaran data bermeterai',
  AGREEMENT: 'Perjanjian Kerja Sama (PKS)',
  VISIT_PHOTO: 'Foto kunjungan',
  SANCTION_LETTER: 'Surat sanksi',
};
export const HUB_APPLICATION_STATUS_LABEL: Record<HubApplicationStatus, string> = {
  SUBMITTED: 'Diajukan',
  NEEDS_REVISION: 'Perlu perbaikan',
  DOCS_OK: 'Dokumen lengkap',
  VISIT_SCHEDULED: 'Kunjungan dijadwalkan',
  VISITED: 'Sudah dikunjungi',
  VERIFIED: 'Direkomendasikan provinsi',
  APPROVED: 'Disetujui',
  AGREEMENT_SIGNED: 'PKS ditandatangani',
  ACTIVE: 'Aktif',
  REJECTED: 'Ditolak',
  WITHDRAWN: 'Dibatalkan pemohon',
};
export const LAND_STATUS_LABEL: Record<LandStatus, string> = { OWN: 'Milik sendiri', LEASE: 'Sewa', LOAN: 'Pinjam pakai' };
export const INTERNET_TYPE_LABEL: Record<InternetType, string> = { CELLULAR: 'Seluler (4G/5G)', FIXED_LINE: 'Internet kabel/serat optik', SATELLITE: 'Satelit', NONE: 'Belum ada' };
export const CHECK_RESULT_LABEL: Record<CheckResult, string> = { PASS: 'Sesuai', FAIL: 'Tidak sesuai', NOT_APPLICABLE: 'Tidak berlaku' };
export const REJECT_REASON_LABEL: Record<RejectReasonCode, string> = {
  INCOMPLETE_DOCUMENTS: 'Dokumen persyaratan tidak lengkap',
  INVALID_DOCUMENTS: 'Dokumen persyaratan tidak sah atau tidak sesuai',
  FALSE_INFORMATION: 'Data yang disampaikan tidak benar',
  SITE_UNSUITABLE: 'Lokasi tidak memenuhi syarat penyimpanan',
  SAFETY_NOT_MET: 'Persyaratan keselamatan tidak terpenuhi',
  AREA_ALREADY_SERVED: 'Wilayah sudah terlayani Hub lain secara memadai',
  NO_RESPONSE: 'Pemohon tidak menanggapi permintaan perbaikan',
  REVISION_LIMIT: 'Batas perbaikan telah terlampaui',
  DUPLICATE_APPLICATION: 'Pendaftaran ganda',
  IMPERSONATION: 'Identitas penanggung jawab digunakan tanpa hak',
  OTHER: 'Alasan lain',
};
export const HUB_STATUS_LABEL: Record<HubStatus, string> = { ACTIVE: 'Aktif', SUSPENDED: 'Dihentikan sementara', TERMINATED: 'Kemitraan diputus' };
export const SANCTION_LABEL: Record<SanctionKind, string> = {
  WARNING_1: 'Surat peringatan 1',
  WARNING_2: 'Surat peringatan 2',
  SUSPEND: 'Penghentian sementara',
  REINSTATE: 'Pemulihan operasional',
  TERMINATE: 'Pemutusan kemitraan',
};
/** The revisable fields as the applicant and the reviewer read them. */
export const REVISABLE_FIELD_LABEL: Record<RevisableField, string> = {
  entityName: 'Nama badan usaha',
  entityBusinessId: 'NIB',
  entityIndustryCode: 'KBLI',
  entityTaxId: 'NPWP badan usaha',
  entityDeedNumber: 'Nomor akta',
  picName: 'Nama penanggung jawab',
  picNationalId: 'NIK penanggung jawab',
  picPhone: 'Nomor telepon',
  picEmail: 'Email',
  picTaxId: 'NPWP penanggung jawab',
  siteVillageCode: 'Desa/kelurahan lokasi',
  siteAddress: 'Alamat lokasi',
  siteLat: 'Titik koordinat lokasi (lintang)',
  siteLng: 'Titik koordinat lokasi (bujur)',
  landStatus: 'Status lahan',
  landValidUntil: 'Masa berlaku sewa/pinjam pakai',
  siteAreaM2: 'Luas ruang penyimpanan',
  storageCapacity: 'Kapasitas penyimpanan',
  nearestFacility: 'Bangunan umum terdekat',
  nearestFacilityDistanceM: 'Jarak ke bangunan umum terdekat',
  gatewayId: 'ID Reader',
  readerModel: 'Model Reader',
  internetType: 'Jenis koneksi internet',
  signalNote: 'Keterangan sinyal',
  bankName: 'Nama bank',
  bankAccount: 'Nomor rekening',
  bankHolder: 'Nama pemilik rekening',
};

// --- households and orders (Phase 3) ---
export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PLACED: 'Baru',
  ACCEPTED: 'Diterima Hub',
  OUT_FOR_DELIVERY: 'Dalam pengantaran',
  READY_FOR_PICKUP: 'Siap diambil',
  DELIVERY_FAILED: 'Gagal antar',
  COMPLETED: 'Selesai',
  CANCELLED: 'Dibatalkan',
};
export const ORDER_KIND_LABEL: Record<OrderKind, string> = { FIRST_ISSUE: 'Paket perdana', EXCHANGE: 'Tukar tabung' };
export const FULFILMENT_LABEL: Record<Fulfilment, string> = { DELIVERY: 'Antar ke rumah', PICKUP: 'Ambil di Hub' };
export const ORDER_CHANNEL_LABEL: Record<OrderChannel, string> = { APP: 'Aplikasi', COUNTER: 'Loket Hub' };
export const EMPTY_CONDITION_LABEL: Record<EmptyCondition, string> = { GOOD: 'Baik', DAMAGED: 'Rusak', MISSING: 'Tidak ada tabung kosong' };
export const ORDER_ACTOR_LABEL: Record<OrderActor, string> = { CITIZEN: 'Warga', STAFF: 'Petugas', SYSTEM: 'Sistem' };
export const ORDER_CANCEL_REASON_LABEL: Record<OrderCancelReason, string> = {
  CITIZEN_REQUEST: 'Atas permintaan warga',
  OUT_OF_STOCK: 'Stok tabung isi habis',
  ADDRESS_UNREACHABLE: 'Alamat tidak terjangkau',
  NOT_AT_HOME: 'Penerima tidak di rumah',
  DUPLICATE: 'Pesanan ganda',
  HUB_UNAVAILABLE: 'Hub tidak beroperasi',
  SLA_BREACHED: 'Melewati batas waktu pengantaran',
  OTHER: 'Lainnya',
};
export const DELIVERY_FAILURE_LABEL: Record<DeliveryFailure, string> = {
  NOT_AT_HOME: 'Penerima tidak di rumah',
  CYLINDER_REFUSED: 'Tabung ditolak warga',
  NO_PAYMENT: 'Pembayaran tidak tersedia',
  ADDRESS_NOT_FOUND: 'Alamat tidak ditemukan',
  OTHER: 'Lainnya',
};
/** How a cylinder was identified on a phone. */
export const ID_METHOD_LABEL: Record<IdMethod, string> = { QR: 'QR', PHONE_READER: 'Reader HP (OTG)', MANUAL: 'Ketik manual' };

export const HOUSEHOLD_STATUS_LABEL: Record<HouseholdStatus, string> = {
  PENDING_VERIFICATION: 'Menunggu verifikasi',
  REGISTERED: 'Terdaftar',
  REJECTED: 'Ditolak',
  SUSPENDED: 'Ditangguhkan',
};
export const HOUSEHOLD_SOURCE_LABEL: Record<HouseholdSource, string> = { SELF: 'Daftar mandiri', IMPORT: 'Daftar penerima', COUNTER: 'Daftar di loket Hub' };
export const CITIZEN_ROLE_LABEL: Record<CitizenRole, string> = { HEAD: 'Kepala keluarga', MEMBER: 'Anggota keluarga' };
